import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";

import {
  AttributeCardSelector,
  BasicDetailsStep,
  PermissionToggleCard,
  PreferencesStep,
  StickyModalFooter,
  WizardModalLayout,
} from "./wizard/WizardComponents";
import {
  defaultPreferences,
  defaultSubAdminPermissions,
  LANGUAGES,
  normalizeUserId,
  subAdminPermissionsSchema,
  TIMEZONES,
  UNITS,
} from "./permissionsDefaults";
import { useCreateUserMutation, useEditUserMutation } from "../../redux/admin/adminApi";

const ATTRIBUTE_OPTIONS = [
  { key: "notificationTokens", label: "Notification Tokens", type: "number" },
  { key: "currency", label: "Currency", type: "text" },
  { key: "unitPrice", label: "Unit Price", type: "number" },
  { key: "managerEmailAddress", label: "Manager Email", type: "email" },
  { key: "managerContactNumber", label: "Manager Contact", type: "tel" },
  { key: "remarks", label: "Remarks", type: "text" },
  { key: "roleAttribute", label: "Role", type: "select", options: ["Parents", "Subuser", "Relay hide"] },
  { key: "video", label: "Video", type: "checkbox" },
  { key: "useExternalMap", label: "Use External Map", type: "checkbox" },
  { key: "mapKey", label: "Map Key", type: "text" },
  { key: "razorpayKey", label: "Razorpay Key", type: "text" },
];

const ATTRIBUTE_MAP = ATTRIBUTE_OPTIONS.reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {});

const buildParentAllowedForSubAdminSchema = (currentUser) => {
  if (currentUser?.permissions && typeof currentUser.permissions === "object") {
    return currentUser.permissions;
  }

  const devicePermissions = currentUser?.devicePermissions || {};
  const userPermissions = currentUser?.userPermissions || {};

  return {
    device: {
      add: devicePermissions.create === true,
      edit: devicePermissions.update === true,
      delete: devicePermissions.delete === true,
      disable: devicePermissions.update === true,
      renew: devicePermissions.update === true,
    },
    user: {
      add: userPermissions.create === true,
      dealer: userPermissions.create === true,
      edit: userPermissions.update === true,
      delete: userPermissions.delete === true,
    },
    dealer: {
      add: userPermissions.create === true,
      edit: userPermissions.update === true,
      delete: userPermissions.delete === true,
      renew: devicePermissions.update === true,
      assign: userPermissions.assign !== undefined ? userPermissions.assign === true : true,
    },
    notification: { add: true },
    dataPush: { allow: true },
    sensors: { allow: true },
    summaryEdit: { allow: true },
    deviceTextbox: { allow: true },
    other: {
      disabled: true,
      limitCommands: devicePermissions.command !== undefined ? devicePermissions.command === true : true,
      readonly: true,
      disableReports: true,
      dontUseDealerLicense: true,
      deviceReadonly: true,
      stopParkingAlerts: true,
    },
    limits: {
      deviceLimit: true,
      userLimit: true,
    },
  };
};

const clampBySchema = (schema, parentAllowed, selected) => {
  const out = structuredClone(selected || {});
  schema.forEach((group) => {
    out[group.key] = out[group.key] ?? {};
    const parentGroup = parentAllowed?.[group.key] || {};
    group.items.forEach((item) => {
      const isAllowed = parentGroup?.[item.key] === true;
      out[group.key][item.key] = isAllowed ? Boolean(out[group.key]?.[item.key]) : false;
    });
  });

  out.limits = out.limits ?? {};
  out.limits.deviceLimit =
    parentAllowed?.limits?.deviceLimit === true ? Number(out.limits.deviceLimit || 0) : 0;
  out.limits.userLimit =
    parentAllowed?.limits?.userLimit === true ? Number(out.limits.userLimit || 0) : 0;
  return out;
};

const mapLegacySubAdminPermissions = (user) => ({
  device: {
    add: user?.devicePermissions?.create === true,
    disable: user?.devicePermissions?.update === true && user?.permissions?.device?.disable === true,
    edit: user?.devicePermissions?.update === true,
    delete: user?.devicePermissions?.delete === true,
    renew: user?.permissions?.device?.renew === true,
  },
  user: {
    add: user?.userPermissions?.create === true,
    dealer: user?.permissions?.user?.dealer === true || user?.userPermissions?.assign === true,
    edit: user?.userPermissions?.update === true,
    delete: user?.userPermissions?.delete === true,
  },
  dealer: {
    add: user?.permissions?.dealer?.add === true || user?.userPermissions?.create === true,
    edit: user?.permissions?.dealer?.edit === true || user?.userPermissions?.update === true,
    delete: user?.permissions?.dealer?.delete === true || user?.userPermissions?.delete === true,
    renew: user?.permissions?.dealer?.renew === true,
    assign: user?.permissions?.dealer?.assign === true || user?.userPermissions?.assign === true,
  },
  notification: { add: user?.permissions?.notification?.add === true },
  dataPush: { allow: user?.permissions?.dataPush?.allow === true },
  sensors: { allow: user?.permissions?.sensors?.allow === true },
  summaryEdit: { allow: user?.permissions?.summaryEdit?.allow === true },
  deviceTextbox: { allow: user?.permissions?.deviceTextbox?.allow === true },
  other: {
    disabled: user?.permissions?.other?.disabled === true,
    limitCommands:
      user?.permissions?.other?.limitCommands === true ||
      user?.devicePermissions?.command === false,
    readonly: user?.permissions?.other?.readonly === true,
    disableReports: user?.permissions?.other?.disableReports === true,
    dontUseDealerLicense: user?.permissions?.other?.dontUseDealerLicense === true,
    deviceReadonly: user?.permissions?.other?.deviceReadonly === true,
    stopParkingAlerts: user?.permissions?.other?.stopParkingAlerts === true,
  },
  limits: {
    deviceLimit: Number(user?.permissions?.limits?.deviceLimit || 0),
    userLimit: Number(user?.permissions?.limits?.userLimit || 0),
  },
});

const toSubAdminFormState = (user) => {
  if (!user) {
    return {
      username: "",
      email: "",
      mobileNo: "",
      password: "",
      uniqueId: "",
      preferences: structuredClone(defaultPreferences),
      permissions: structuredClone(defaultSubAdminPermissions),
      attributes: [],
    };
  }

  return {
    username: user.username || "",
    email: user.email || "",
    mobileNo: user.mobileNo || "",
    password: "",
    uniqueId: user.uniqueId || "",
    preferences: {
      ...defaultPreferences,
      ...(user.preferences || {}),
      timeZone:
        user.preferences?.timeZone ||
        user.preferences?.timezone ||
        defaultPreferences.timeZone,
      language: user.preferences?.language || defaultPreferences.language,
    },
    permissions: user.permissions
      ? { ...structuredClone(defaultSubAdminPermissions), ...structuredClone(user.permissions) }
      : mapLegacySubAdminPermissions(user),
    attributes: Array.isArray(user.attributes) ? structuredClone(user.attributes) : [],
  };
};

const createPreferenceFields = () => [
  { path: "preferences.speedUnit", label: "Speed Unit", options: UNITS.speed, required: true },
  { path: "preferences.distanceUnit", label: "Distance Unit", options: UNITS.distance, required: true },
  { path: "preferences.altitudeUnit", label: "Altitude Unit", options: UNITS.altitude, required: true },
  { path: "preferences.volumeUnit", label: "Volume Unit", options: UNITS.volume, required: true },
  { path: "preferences.timeZone", label: "Time Zone", options: TIMEZONES, required: true },
  { path: "preferences.language", label: "Language", options: LANGUAGES, required: true },
];

const getBasicStepErrors = (form, mode) => {
  const errors = {};
  if (!form.username?.trim()) errors.username = "Name is required";
  if (!form.uniqueId?.trim()) errors.uniqueId = "User ID is required";
  if (!form.mobileNo?.trim()) errors.mobileNo = "Mobile is required";
  if (!form.email?.trim()) errors.email = "Email is required";
  if (mode !== "edit" && !form.password?.trim()) errors.password = "Password is required";
  return errors;
};

const toPermissionCardItems = (groupKey) =>
  subAdminPermissionsSchema
    .find((group) => group.key === groupKey)
    ?.items.map((item) => ({ ...item, groupKey })) || [];

export default function AddSubAdminModal({
  open,
  onClose,
  onSuccess,
  mode = "create",
  initialData = null,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [createUser, { isLoading }] = useCreateUserMutation();
  const [editUser, { isLoading: isEditing }] = useEditUserMutation();
  const currentUser = useSelector((state) => state?.auth?.user);

  const parentAllowed = useMemo(
    () => buildParentAllowedForSubAdminSchema(currentUser),
    [currentUser]
  );

  const [error, setError] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [showStepErrors, setShowStepErrors] = useState(false);
  const [form, setForm] = useState(() => toSubAdminFormState(initialData));
  const [selectedAttributeKey, setSelectedAttributeKey] = useState("");
  const [draftAttributeValue, setDraftAttributeValue] = useState("");
  const [advancedExpanded, setAdvancedExpanded] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(toSubAdminFormState(initialData));
    setError("");
    setCurrentStep(0);
    setShowStepErrors(false);
    setSelectedAttributeKey("");
    setDraftAttributeValue("");
    setAdvancedExpanded(false);
  }, [open, initialData]);

  const steps = [
    { key: "basic", title: "Basic Details", description: "Account information" },
    { key: "preferences", title: "Preferences", description: "Default settings" },
    { key: "permissions", title: "Permissions", description: "Access and controls" },
  ];

  const onChange = (path, value) => {
    setError("");
    if (path === "uniqueId") value = normalizeUserId(value);
    setForm((prev) => {
      const keys = path.split(".");
      const next = structuredClone(prev);
      let cursor = next;
      for (let index = 0; index < keys.length - 1; index += 1) {
        cursor[keys[index]] = cursor[keys[index]] ?? {};
        cursor = cursor[keys[index]];
      }
      cursor[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const usedAttributeKeys = (form.attributes || []).map((attr) => attr.key);
  const selectAttribute = (key) => {
    const config = ATTRIBUTE_MAP[key];
    setSelectedAttributeKey(key);
    setDraftAttributeValue(config?.type === "checkbox" ? false : "");
  };

  const addAttribute = () => {
    if (!selectedAttributeKey || usedAttributeKeys.includes(selectedAttributeKey)) return;
    const selectedConfig = ATTRIBUTE_MAP[selectedAttributeKey];
    if (!selectedConfig) return;

    setForm((prev) => ({
      ...prev,
      attributes: [
        ...(prev.attributes || []),
        {
          key: selectedConfig.key,
          label: selectedConfig.label,
          type: selectedConfig.type,
          value: selectedConfig.type === "checkbox" ? Boolean(draftAttributeValue) : draftAttributeValue,
        },
      ],
    }));
    setSelectedAttributeKey("");
    setDraftAttributeValue("");
  };

  const updateAttribute = (index, value) => {
    setForm((prev) => {
      const nextAttributes = [...(prev.attributes || [])];
      nextAttributes[index] = { ...nextAttributes[index], value };
      return { ...prev, attributes: nextAttributes };
    });
  };

  const removeAttribute = (index) => {
    setForm((prev) => ({
      ...prev,
      attributes: (prev.attributes || []).filter((_, attrIndex) => attrIndex !== index),
    }));
  };

  const togglePermission = (groupKey, itemKey) => {
    if (parentAllowed?.[groupKey]?.[itemKey] !== true) return;
    setForm((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [groupKey]: {
          ...prev.permissions?.[groupKey],
          [itemKey]: !prev.permissions?.[groupKey]?.[itemKey],
        },
      },
    }));
  };

  const basicErrors = useMemo(() => getBasicStepErrors(form, mode), [form, mode]);
  const visibleBasicErrors = showStepErrors && currentStep === 0 ? basicErrors : {};

  const handleNext = () => {
    if (currentStep === 0 && Object.keys(basicErrors).length) {
      setShowStepErrors(true);
      return;
    }
    setShowStepErrors(false);
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleSubmit = async () => {
    setError("");
    if (Object.keys(basicErrors).length) {
      setCurrentStep(0);
      setShowStepErrors(true);
      return;
    }

    const clampedPermissions = clampBySchema(
      subAdminPermissionsSchema,
      parentAllowed,
      form.permissions
    );

    const payload = {
      username: form.username.trim(),
      email: form.email.trim().toLowerCase(),
      mobileNo: String(form.mobileNo || "").trim(),
      ...(form.password?.trim() ? { password: form.password } : {}),
      role: "sub_admin",
      uniqueId: form.uniqueId ? normalizeUserId(form.uniqueId) : undefined,
      preferences: form.preferences,
      permissions: clampedPermissions,
      attributes: form.attributes,
    };

    try {
      const response =
        mode === "edit" && initialData?._id
          ? await editUser({ userId: initialData._id, ...payload }).unwrap()
          : await createUser(payload).unwrap();

      onSuccess?.(response);
      onClose?.();
    } catch (submitError) {
      setError(
        submitError?.data?.msg ||
          submitError?.message ||
          (mode === "edit" ? "Failed to update admin" : "Failed to create admin")
      );
    }
  };

  const detailFields = [
    { path: "username", label: "Name", required: true },
    {
      path: "uniqueId",
      label: "User ID",
      required: true,
      helperText: "No spaces. Only a-z, 0-9, dot, underscore, dash.",
    },
    { path: "mobileNo", label: "Mobile", required: true, inputProps: { inputMode: "numeric" } },
    { path: "email", label: "Email", required: true, type: "email" },
    {
      path: "password",
      label: "Password",
      type: "password",
      required: mode !== "edit",
      helperText: mode === "edit" ? "Leave blank to keep current password." : "Use a secure password.",
      md: 12,
    },
  ];

  return (
    <WizardModalLayout
      open={open}
      onClose={onClose}
      icon={AdminPanelSettingsOutlinedIcon}
      title={mode === "edit" ? "Edit Admin" : "Create Admin"}
      subtitle={
        mode === "edit"
          ? "Update admin access in 3 guided steps."
          : "Create a new admin account in 3 simple steps."
      }
      steps={steps}
      currentStep={currentStep}
      onStepChange={setCurrentStep}
      error={error}
      helpTitle="Need Help?"
      helpText="Complete the basic details first. You can then review preferences, permissions, and extra admin fields before saving."
      fullScreen={fullScreen}
      maxWidth="xl"
      footer={
        <StickyModalFooter
          currentStep={currentStep}
          steps={steps}
          loading={isLoading || isEditing}
          onBack={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
          onCancel={onClose}
          onNext={handleNext}
          onSubmit={handleSubmit}
          submitLabel={mode === "edit" ? "Save Admin" : "Create Admin"}
        />
      }
    >
      {currentStep === 0 ? (
        <BasicDetailsStep
          title="Admin Information"
          description="Enter the basic information for the admin account."
          form={form}
          onChange={onChange}
          fields={detailFields}
          errors={visibleBasicErrors}
          tip="Admin account creation keeps the same role, payload, hierarchy permissions, and parent-limited behavior."
        />
      ) : null}

      {currentStep === 1 ? (
        <PreferencesStep
          form={form}
          onChange={onChange}
          errors={{}}
          fields={createPreferenceFields()}
          note="These preferences still go to the same backend payload and remain editable only where your current logic allows."
        />
      ) : null}

      {currentStep === 2 ? (
        <Box sx={{ display: "grid", gap: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <PermissionToggleCard
                title="Device Access"
                description="Control device-related actions available to this admin."
                items={toPermissionCardItems("device")}
                values={form.permissions}
                parentAllowed={parentAllowed}
                onToggle={togglePermission}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PermissionToggleCard
                title="User Access"
                description="User and dealer management rights remain parent-limited."
                items={[...toPermissionCardItems("user"), ...toPermissionCardItems("dealer")]}
                values={form.permissions}
                parentAllowed={parentAllowed}
                onToggle={togglePermission}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PermissionToggleCard
                title="Data & Alerts"
                description="Notification, data push, and sensor controls."
                items={[
                  ...toPermissionCardItems("notification"),
                  ...toPermissionCardItems("dataPush"),
                  ...toPermissionCardItems("sensors"),
                ]}
                values={form.permissions}
                parentAllowed={parentAllowed}
                onToggle={togglePermission}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PermissionToggleCard
                title="Other Access"
                description="Summary editing and device text box visibility."
                items={[
                  ...toPermissionCardItems("summaryEdit"),
                  ...toPermissionCardItems("deviceTextbox"),
                ]}
                values={form.permissions}
                parentAllowed={parentAllowed}
                onToggle={togglePermission}
              />
            </Grid>
          </Grid>

          <Accordion
            expanded={advancedExpanded}
            onChange={(_, expanded) => setAdvancedExpanded(expanded)}
            sx={{
              borderRadius: 4,
              border: "1px solid #eadcff",
              boxShadow: "none",
              backgroundColor: alpha(theme.palette.secondary.light, 0.12),
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box>
                <Typography sx={{ fontWeight: 800 }}>Advanced Settings</Typography>
                <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                  Additional security, readonly, and limit settings.
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <PermissionToggleCard
                    title="Advanced Controls"
                    description="Locked permissions stay greyed out when not allowed by the parent user."
                    items={toPermissionCardItems("other")}
                    values={form.permissions}
                    parentAllowed={parentAllowed}
                    onToggle={togglePermission}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      borderRadius: 4,
                      border: "1px solid #e5e7eb",
                      backgroundColor: "#fff",
                      p: 2.25,
                      display: "grid",
                      gap: 2,
                      height: "100%",
                    }}
                  >
                    <Typography sx={{ fontSize: 17, fontWeight: 800 }}>Limits</Typography>
                    <TextField
                      label="Device Limit"
                      type="number"
                      value={form.permissions?.limits?.deviceLimit ?? 0}
                      onChange={(event) =>
                        onChange("permissions.limits.deviceLimit", Number(event.target.value || 0))
                      }
                      disabled={parentAllowed?.limits?.deviceLimit !== true}
                      inputProps={{ min: 0 }}
                    />
                    <TextField
                      label="User Limit"
                      type="number"
                      value={form.permissions?.limits?.userLimit ?? 0}
                      onChange={(event) =>
                        onChange("permissions.limits.userLimit", Number(event.target.value || 0))
                      }
                      disabled={parentAllowed?.limits?.userLimit !== true}
                      inputProps={{ min: 0 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <AttributeCardSelector
            options={ATTRIBUTE_OPTIONS}
            attributeDefinitions={ATTRIBUTE_OPTIONS}
            disabledKeys={usedAttributeKeys}
            selectedKey={selectedAttributeKey}
            onSelectKey={selectAttribute}
            draftValue={draftAttributeValue}
            onDraftValueChange={setDraftAttributeValue}
            onAdd={addAttribute}
            attributes={form.attributes || []}
            onUpdateAttribute={updateAttribute}
            onRemoveAttribute={removeAttribute}
          />
        </Box>
      ) : null}
    </WizardModalLayout>
  );
}
